/**
 * The first screen dive: Meta's best-running ad ("this is a work of art", Ace / EU-Core, Sep 2026),
 * unchanged, with two dives into the phone. Its source is a 720x1280 clip with captions burned in, so
 * the dives run on stills with the caption painted out, and the caption is set again on top.
 *
 * The trip is the one on the phone in the clip (a Philippines trip): El Nido's places from the
 * knowledge base, the Palawan leg with the nights and transport the app shows. The media paths point
 * into the gitignored public/featureintro/media/ads folder for a local render; on the render box every
 * one of them is an https URL (see the `ad-video` skill in stippl-knowledge).
 */
import type { ScreenDiveProps } from "./ScreenDive";

const M = "featureintro/media/ads/work-of-art/";

export const WORK_OF_ART_DIVE: ScreenDiveProps = {
	base: `${M}work-of-art.mp4`,
	baseSize: { w: 720, h: 1280 },
	durationInFrames: 453,
	baseVolume: 1,
	captions: [
		{ text: "this is a work of art", from: 138, to: 211, emoji: "✨" },
		{ text: "this is a work of art", from: 272, to: 355, emoji: "✨" },
	],
	captionY: 598,
	dives: [
		{
			// after the tap on Nacpan Beach: into the El Nido stop, back out on the next card
			from: 138,
			end: 211,
			dive: [138, 154],
			back: [194, 208],
			quadIn: { cx: 407, cy: 687, w: 414, h: 968, rot: 13.4 },
			quadOut: { cx: 395, cy: 683, w: 418, h: 968, rot: 13.4 },
			stillIn: `${M}f138.png`,
			stillOut: `${M}f208.png`,
			scene: {
				kind: "places",
				camera: { lng: 119.374, lat: 11.233, zoom: 11.55, zoomTo: 11.75, pitch: 34, bearing: -8, padTop: 700, padBottom: 350 },
				places: [
					{ name: "Nacpan Beach", ll: [119.4278, 11.3212], photo: `${M}nacpan.jpg`, glyph: "island", cardDx: -0.35 },
					{ name: "Cadlao Island", ll: [119.345, 11.2147], photo: `${M}cadlao.jpg`, glyph: "island", cardDx: -0.5, captionSide: -1 },
					{ name: "Big Lagoon", ll: [119.321, 11.1544], photo: `${M}biglagoon.jpg`, glyph: "island", cardDx: 0.5 },
					{ name: "Las Cabañas", ll: [119.3945, 11.1448], photo: `${M}cabanas.jpg`, glyph: "island", cardDx: -0.3 },
				],
				stopCard: { title: "El Nido", dates: "Wed 06 Mar - Sat 09 Mar", nights: 3 },
			},
		},
		{
			// into the travel journal: the Palawan leg draws itself, El Nido opens, cut on the beat
			from: 272,
			end: 355,
			dive: [272, 288],
			quadIn: { cx: 315, cy: 660, w: 448, h: 1040, rot: 11.5 },
			stillIn: `${M}f272.png`,
			scene: {
				kind: "route",
				camera: { lng: 119.86, lat: 12.17, zoom: 7.45, zoomTo: 7.15, pitch: 24, bearing: 0, padTop: 780, padBottom: 110 },
				stops: [
					{ name: "Manila", ll: [120.9842, 14.5995], nights: 2, photo: `${M}manila.jpg`, cardDx: 0.35 },
					{ name: "Coron", ll: [120.2043, 11.9986], nights: 2, photo: `${M}coron.jpg`, cardDx: 0.55, captionSide: 1 },
					{ name: "El Nido", ll: [119.3927, 11.1795], nights: 3, photo: `${M}elnido.jpg`, cardDx: -0.6, captionSide: 1 },
					{ name: "Port Barton", ll: [119.1668, 10.4056], nights: 3, photo: `${M}portbarton.jpg`, cardDx: -0.55, captionSide: -1 },
					{ name: "Linapacan", ll: [119.8697, 11.4859], nights: 3, cardDx: 0.5, captionSide: 1 },
					{ name: "Puerto Princesa", ll: [118.7384, 9.7392], nights: 1, photo: `${M}puerto.jpg`, cardDx: 0.5, captionSide: 1 },
				],
				// as in the app: plane, boat, 3h 47m by road, then two legs with no transport chosen yet
				legs: ["plane", "boat", "bus", null, null],
				open: { stop: 2, clip: `${M}elnido-clip.mp4`, at: 330 },
			},
		},
	],
};
